import { User } from "@/lib/client-auth";
import { authorize, logout } from "@/services/auth";
import { useWorkspaceStore } from "@/store/workspace/workspace-store";
import { StateCreator } from "zustand/vanilla";
import { UserStore } from "../../user-store";

export interface UserAuthAction {
  logout: () => Promise<void>;
  authorize: () => Promise<void>;
  setUser: (user: User | undefined) => void;
  setAuthenticated: (isSignedIn: boolean) => void;
  setInitialized: (isLoaded: boolean) => void;
  beginWorkspaceEntitlementsLoad: (workspaceKey: string) => void;
  setWorkspaceEntitlements: (workspaceKey: string, user: User) => void;
  failWorkspaceEntitlementsLoad: (workspaceKey: string) => void;
}

export const createAuthSlice: StateCreator<
  UserStore,
  [["zustand/devtools", never]],
  [],
  UserAuthAction
> = (set) => ({
  logout: async () => {
    // Clear workspace list before logout, but preserve the user's last-selected workspace
    // so we can restore it after login when we refetch the workspace list.
    useWorkspaceStore.getState().clearWorkspace({ preserveSelection: true });

    // Use runtime window.location.origin instead of build-time env var
    const host = typeof window !== "undefined" ? window.location.origin : "";
    if (host) {
      logout({ post_logout_redirect_uri: host });
      return;
    }
    logout({});
  },
  authorize: async () => {
    const next =
      typeof window !== "undefined"
        ? window.location.pathname +
          window.location.search +
          window.location.hash
        : "/dashboard";
    authorize({
      next,
      referer: typeof window !== "undefined" ? window.location.href : undefined,
      project_id: "1",
    });
  },
  setUser: (user) => {
    set({ user });
  },
  setAuthenticated: (isSignedIn) => {
    set({ isSignedIn });
  },
  setInitialized: (isLoaded) => {
    set({ isLoaded });
  },
  beginWorkspaceEntitlementsLoad: () => {
    set({
      activeEntitlementWorkspaceKey: undefined,
      isEntitlementsLoading: true,
    });
  },
  setWorkspaceEntitlements: (workspaceKey, user) => {
    const screens = user.screens ?? [];
    const features = user.features ?? [];
    set((state) => ({
      user,
      entitlementsByWorkspace: {
        ...state.entitlementsByWorkspace,
        [workspaceKey]: { screens, features },
      },
      activeEntitlementWorkspaceKey: workspaceKey,
      isEntitlementsLoading: false,
    }));
  },
  failWorkspaceEntitlementsLoad: (workspaceKey) => {
    set((state) => ({
      user: state.user
        ? { ...state.user, screens: [], features: [] }
        : state.user,
      entitlementsByWorkspace: {
        ...state.entitlementsByWorkspace,
        [workspaceKey]: { screens: [], features: [] },
      },
      activeEntitlementWorkspaceKey: workspaceKey,
      isEntitlementsLoading: false,
    }));
  },
});
